import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, RefreshCw, TrendingDown,
  Repeat, DollarSign, Calendar,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const INK = "#0B192C";
const FLAME = "#FF4500";
const HORIZON = 10;            // analysis window, years
const TRAD_PRICE = 500;        // low-quality mat unit price
const TRAD_LIFE = 1.5;         // service life, years, before replacement
const BBM_PRICE = 1900;        // engineered mat unit price
const FLEET = 200;             // representative fleet size

/* ---------- unit economics model (per single mat) ---------- */
const events = [];
for (let t = 0; t < HORIZON - 0.001; t += TRAD_LIFE) events.push(Number(t.toFixed(2)));
const tradPurchases = events.length;
const tradLifetime = tradPurchases * TRAD_PRICE;
const bbmLifetime = BBM_PRICE;
const savePerMat = tradLifetime - bbmLifetime;
const fleetTrad = tradLifetime * FLEET;
const fleetBBM = bbmLifetime * FLEET;
const fleetSave = savePerMat * FLEET;
const tradDeployPerYear = 8;
const tradDeploys = tradDeployPerYear * TRAD_LIFE;
const bbmDeploys = tradDeployPerYear * HORIZON;
const tradCPD = TRAD_PRICE / tradDeploys;
const bbmCPD = BBM_PRICE / bbmDeploys;

/* ---------- chart geometry ---------- */
const W = 720, H = 300, PAD_L = 54, PAD_R = 20, PAD_T = 22, PAD_B = 36;
const yMax = Math.ceil((tradLifetime + 300) / 500) * 500;
const px = (yr) => PAD_L + (yr / HORIZON) * (W - PAD_L - PAD_R);
const py = (v) => H - PAD_B - (v / yMax) * (H - PAD_T - PAD_B);
let tradPath = "";
events.forEach((t, i) => {
  const level = (i + 1) * TRAD_PRICE;
  const end = i < events.length - 1 ? events[i + 1] : HORIZON;
  if (i === 0) tradPath += `M ${px(t)} ${py(level)} `;
  else tradPath += `L ${px(t)} ${py(i * TRAD_PRICE)} L ${px(t)} ${py(level)} `;
  tradPath += `L ${px(end)} ${py(level)} `;
});
const breakEvenYr = TRAD_LIFE * Math.ceil(BBM_PRICE / TRAD_PRICE);
const usd = (n) => "$" + n.toLocaleString();
const CPD_MAX = Math.max(tradCPD, bbmCPD);

const ROWS = [
  { k: "Unit purchase price", t: usd(TRAD_PRICE), b: usd(BBM_PRICE) },
  { k: "Service life", t: `${TRAD_LIFE} yrs`, b: `${HORIZON}+ yrs` },
  { k: "Deployments / life", t: `~${tradDeploys}`, b: `~${bbmDeploys}` },
  { k: `Re-buys / ${HORIZON} yrs`, t: `${tradPurchases}×`, b: "1×" },
  { k: `10-yr spend / mat`, t: usd(tradLifetime), b: usd(bbmLifetime) },
  { k: "Cost / deployment", t: usd(Math.round(tradCPD)), b: usd(Math.round(bbmCPD)) },
];

const KPIS = [
  { icon: TrendingDown, v: `${Math.round((1 - bbmCPD / tradCPD) * 100)}%`, l: "Lower cost / use", flame: true },
  { icon: Repeat, v: `${tradPurchases}×`, l: "Budget re-buys" },
  { icon: Calendar, v: `~${breakEvenYr}y`, l: "Break-even" },
  { icon: DollarSign, v: usd(savePerMat), l: "Saved / mat", flame: true },
];

export default function MatEconomicsBrief() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to product
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Unit Economics · One-Page Brief</span>
            <a href="/big-bad-mats-roi-brief.pdf" download data-testid="download-roi-brief" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-roi-brief" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ===== A4 LANDSCAPE SHEET ===== */}
      <div className="py-8 print:py-0 flex justify-center">
        <div
          data-testid="roi-brief-sheet"
          className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col"
          style={{ fontSize: "10px" }}
        >
          {/* header */}
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[63%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">Ground Protection · Unit Economics</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[34px]">
                Buy the Mat Once. Not Seven Times.
              </h1>
              <p className="mt-2 text-charcoal leading-snug text-[11px]">
                A procurement view of <span className="font-semibold text-ink">Big Bad Mats</span> as a longer-horizon ROI asset — spreading cost across far more
                deployments and years than fast-deteriorating budget mats, for the <span className="text-flame font-semibold">lowest effective cost per use.</span>
              </p>
            </div>
            {/* KPI chips */}
            <div className="grid grid-cols-2 gap-2 w-[34%]">
              {KPIS.map((s) => (
                <div key={s.l} className="border border-border p-2">
                  <s.icon className={`h-4 w-4 ${s.flame ? "text-flame" : "text-ink/60"}`} />
                  <div className={`mt-1 font-display font-extrabold text-[19px] leading-none ${s.flame ? "text-flame" : "text-ink"}`}>{s.v}</div>
                  <div className="text-[8px] uppercase tracking-wider text-muted-foreground mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>
          </header>

          {/* body: chart (left) + table/fleet (right) */}
          <div className="flex-1 grid grid-cols-5 gap-5 pt-4 min-h-0">
            {/* LEFT */}
            <div className="col-span-3 flex flex-col min-h-0">
              <h2 className="font-display font-bold text-ink text-[13px]">Cumulative Cost of Ownership · per mat</h2>
              <p className="text-muted-foreground text-[10px] mb-1">Budget mats look cheaper on day one — then re-buy on every deterioration cycle. Big Bad Mats stays a single flat line.</p>
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full flex-1" preserveAspectRatio="xMidYMid meet">
                {Array.from({ length: yMax / 500 + 1 }).map((_, i) => {
                  const v = i * 500;
                  return (
                    <g key={v}>
                      <line x1={PAD_L} y1={py(v)} x2={W - PAD_R} y2={py(v)} stroke="#EEF0F2" strokeWidth="1" />
                      <text x={PAD_L - 8} y={py(v) + 3} textAnchor="end" fontSize="10" fill="#9AA3AE" fontFamily="monospace">{usd(v)}</text>
                    </g>
                  );
                })}
                {[0, 2, 4, 6, 8, 10].map((yr) => (
                  <text key={yr} x={px(yr)} y={H - PAD_B + 16} textAnchor="middle" fontSize="10" fill="#9AA3AE" fontFamily="monospace">{yr}y</text>
                ))}
                <line x1={px(breakEvenYr)} y1={PAD_T} x2={px(breakEvenYr)} y2={H - PAD_B} stroke={FLAME} strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />
                <text x={px(breakEvenYr) + 5} y={PAD_T + 11} fontSize="9" fill={FLAME} fontWeight="700">BREAK-EVEN</text>
                <path d={tradPath} fill="none" stroke="#8A93A0" strokeWidth="2.5" />
                <line x1={px(0)} y1={py(BBM_PRICE)} x2={px(HORIZON)} y2={py(BBM_PRICE)} stroke={FLAME} strokeWidth="3" />
                <circle cx={px(0)} cy={py(BBM_PRICE)} r="4" fill={FLAME} />
                <text x={px(HORIZON) - 4} y={py(tradLifetime) - 7} textAnchor="end" fontSize="11" fontWeight="700" fill="#5B6470">{usd(tradLifetime)}</text>
                <text x={px(HORIZON) - 4} y={py(BBM_PRICE) - 7} textAnchor="end" fontSize="11" fontWeight="700" fill={FLAME}>{usd(BBM_PRICE)}</text>
              </svg>
              <div className="flex items-center gap-5 mt-1 text-[10px]">
                <span className="inline-flex items-center gap-1.5"><span className="h-1 w-4 bg-[#8A93A0] inline-block" /> Budget / low-quality mats</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-1 w-4 bg-flame inline-block" /> Big Bad Mats</span>
              </div>

              {/* cost per deployment bars */}
              <div className="mt-3 border-t border-border pt-3">
                <h3 className="font-display font-bold text-ink text-[12px] mb-2">Effective Cost per Deployment — purchase price spread across every reuse</h3>
                {[
                  { label: "Budget mats", val: tradCPD, color: "#8A93A0" },
                  { label: "Big Bad Mats", val: bbmCPD, color: FLAME },
                ].map((d) => (
                  <div key={d.label} className="mb-1.5">
                    <div className="flex justify-between text-[10px] mb-0.5">
                      <span className="text-charcoal">{d.label}</span>
                      <span className="font-mono font-semibold text-ink">{usd(Math.round(d.val))}/use</span>
                    </div>
                    <div className="h-3 bg-muted overflow-hidden">
                      <div className="h-full" style={{ width: `${(d.val / CPD_MAX) * 100}%`, backgroundColor: d.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-span-2 flex flex-col min-h-0 gap-4">
              {/* comparison table */}
              <div className="border border-border">
                <div className="grid grid-cols-3 bg-ink text-white text-[9px] uppercase tracking-wider">
                  <div className="p-2">Metric</div>
                  <div className="p-2 border-l border-white/10">Budget</div>
                  <div className="p-2 border-l border-white/10 text-flame">Big Bad</div>
                </div>
                {ROWS.map((r, i) => (
                  <div key={r.k} className={`grid grid-cols-3 text-[11px] ${i % 2 ? "bg-muted/40" : "bg-white"}`}>
                    <div className="p-2 text-charcoal font-medium">{r.k}</div>
                    <div className="p-2 border-l border-border font-mono text-charcoal">{r.t}</div>
                    <div className="p-2 border-l border-border font-mono font-semibold text-flame">{r.b}</div>
                  </div>
                ))}
              </div>

              {/* fleet ROI callout */}
              <div className="bg-ink text-white p-4 flex-1 flex flex-col justify-center">
                <div className="text-[8px] uppercase tracking-[0.18em] text-white/50">Fleet Impact · {FLEET} mats · {HORIZON}-yr horizon</div>
                <h2 className="mt-1 font-display font-extrabold text-[17px] leading-tight">The absolute ROI of buying quality once.</h2>
                <div className="mt-3 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                  <div className="bg-ink p-2.5">
                    <div className="text-[8px] uppercase tracking-wider text-white/50">Budget spend</div>
                    <div className="mt-1 font-display font-extrabold text-[16px] text-white/85">{usd(fleetTrad)}</div>
                  </div>
                  <div className="bg-ink p-2.5">
                    <div className="text-[8px] uppercase tracking-wider text-white/50">Big Bad spend</div>
                    <div className="mt-1 font-display font-extrabold text-[16px] text-white">{usd(fleetBBM)}</div>
                  </div>
                  <div className="bg-flame p-2.5">
                    <div className="text-[8px] uppercase tracking-wider text-white/80">10-yr savings</div>
                    <div className="mt-1 font-display font-extrabold text-[16px] text-white">{usd(fleetSave)}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2 text-white/60 text-[9px] leading-snug">
                  <RefreshCw className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-px" />
                  Excludes downtime, machine recovery, and site-restoration costs that budget mats incur far more often — widening the real gap further.
                </div>
              </div>
            </div>
          </div>

          {/* footer */}
          <div className="mt-3 pt-2 border-t border-border flex items-center justify-between gap-4">
            <p className="text-[8px] text-muted-foreground leading-tight max-w-[64%]">
              Illustrative unit-economics model — {usd(TRAD_PRICE)} budget mat ({TRAD_LIFE}-yr life) vs {usd(BBM_PRICE)} Big Bad Mats over {HORIZON} yrs at ~{tradDeployPerYear} deployments/yr. Actual figures vary by site, utilization &amp; handling. Not a quote, guarantee, or warranty of any specific result; provided for evaluation only and not financial, engineering, or legal advice. © {new Date().getFullYear()} {COMPANY.legal}.
            </p>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="font-display font-bold text-ink text-[12px] tracking-tight">Run these numbers against your fleet.</div>
                <div className="text-[9px] text-muted-foreground">{COMPANY.phone} · {COMPANY.email}</div>
              </div>
              <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors print:hidden">
                Request ROI Model
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
